import { clsx } from "clsx";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  FileSpreadsheet,
  GitBranchPlus,
  Pencil,
  Plus,
  Trash2,
  Unlink,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Alert, Button, Card, Modal, Spinner } from "@/components/common";
import { AgentSelect } from "@/components/imports/AgentSelect";
import { ManualEntryModal } from "@/components/settlements/ManualEntryModal";
import { SplitCommissionModal } from "@/components/settlements/SplitCommissionModal";
import {
  useAddManualEntry,
  useApproveSettlement,
  useDeleteManualEntry,
  useMonthChecklist,
  useSettlementApprovalPreview,
  useSplitPolicyCommission,
  useUnsplitPolicyCommission,
  useUpdateManualEntry,
} from "@/hooks/useSettlements";
import type {
  ApprovalPolicyLine,
  PolicyReassignment,
} from "@/types/settlement";

interface SettlementApprovalViewProps {
  settlementId: string;
  onClose: () => void;
}

function formatMoney(value: number): string {
  return (
    value.toLocaleString("pl-PL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " zł"
  );
}

function formatPeriod(year: number, month: number): string {
  return new Date(year, month - 1).toLocaleDateString("pl-PL", {
    month: "long",
    year: "numeric",
  });
}

interface InsurerGroup {
  insurerName: string;
  policies: ApprovalPolicyLine[];
  totalPremium: number;
  totalCommission: number;
  totalAgentCommission: number;
}

function groupByInsurer(policies: ApprovalPolicyLine[]): InsurerGroup[] {
  const map = new Map<string, ApprovalPolicyLine[]>();
  for (const policy of policies) {
    const key = policy.insurerName;
    const group = map.get(key);
    if (group) {
      group.push(policy);
    } else {
      map.set(key, [policy]);
    }
  }
  return Array.from(map.entries())
    .map(([insurerName, groupPolicies]) => ({
      insurerName,
      policies: groupPolicies,
      totalPremium: groupPolicies.reduce((s, p) => s + p.premiumAmount, 0),
      totalCommission: groupPolicies.reduce(
        (s, p) => s + p.effectiveCommission,
        0,
      ),
      totalAgentCommission: groupPolicies.reduce(
        (s, p) => s + p.agentCommission,
        0,
      ),
    }))
    .sort((a, b) => a.insurerName.localeCompare(b.insurerName, "pl"));
}

export function SettlementApprovalView({
  settlementId,
  onClose,
}: SettlementApprovalViewProps) {
  const {
    data: preview,
    isLoading,
    isError,
    error,
  } = useSettlementApprovalPreview(settlementId);
  const approve = useApproveSettlement();
  // The API refuses approval until every insurer of the month is accounted for.
  const { data: checklist } = useMonthChecklist(
    preview?.year ?? 0,
    preview?.month ?? 0,
    !!preview,
  );
  const missingInsurers =
    checklist?.items.filter((i) => i.status === "Missing") ?? [];
  const monthIncomplete = !!checklist && !checklist.isComplete;
  const addManualEntry = useAddManualEntry();
  const updateManualEntry = useUpdateManualEntry();
  const deleteManualEntry = useDeleteManualEntry();
  const splitPolicy = useSplitPolicyCommission();
  const unsplitPolicy = useUnsplitPolicyCommission();

  // policyId → newAgentId
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<ApprovalPolicyLine | null>(
    null,
  );
  const [deletingPolicy, setDeletingPolicy] =
    useState<ApprovalPolicyLine | null>(null);
  const [splittingPolicy, setSplittingPolicy] =
    useState<ApprovalPolicyLine | null>(null);
  const [unsplittingPolicy, setUnsplittingPolicy] =
    useState<ApprovalPolicyLine | null>(null);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  const groups = useMemo(
    () => (preview ? groupByInsurer(preview.policies) : []),
    [preview],
  );

  const allCollapsed =
    groups.length > 0 && collapsedGroups.size === groups.length;

  function toggleGroup(insurerName: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(insurerName)) {
        next.delete(insurerName);
      } else {
        next.add(insurerName);
      }
      return next;
    });
  }

  function toggleAllGroups() {
    if (allCollapsed) {
      setCollapsedGroups(new Set());
    } else {
      setCollapsedGroups(new Set(groups.map((g) => g.insurerName)));
    }
  }

  function handleOverride(policyId: string, newAgentId: string) {
    setOverrides((prev) => {
      if (!newAgentId) {
        const next = { ...prev };
        delete next[policyId];
        return next;
      }
      return { ...prev, [policyId]: newAgentId };
    });
  }

  function clearOverride(policyId: string) {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[policyId];
      return next;
    });
  }

  function handleApprove() {
    const policyOverrides: PolicyReassignment[] = Object.entries(overrides).map(
      ([policyId, newAgentId]) => ({ policyId, newAgentId }),
    );
    approve.mutate(
      { id: settlementId, policyOverrides },
      {
        onSuccess: () => {
          toast.success("Rozliczenie zatwierdzone i e-mail wysłany.");
          onClose();
        },
      },
    );
  }

  function confirmDeleteManualEntry() {
    if (!preview || !deletingPolicy) return;
    deleteManualEntry.mutate(
      { settlementId: preview.settlementId, policyId: deletingPolicy.policyId },
      {
        onSuccess: () => {
          setDeletingPolicy(null);
          toast.success("Wpis usunięty");
        },
      },
    );
  }

  function confirmUnsplit() {
    if (!preview || !unsplittingPolicy) return;
    unsplitPolicy.mutate(
      {
        settlementId: preview.settlementId,
        policyId: unsplittingPolicy.policyId,
      },
      {
        onSuccess: () => {
          setUnsplittingPolicy(null);
          toast.success("Podział cofnięty — rozliczenia zostały przeliczone.");
        },
        onError: (err) => {
          toast.error(
            (err as Error).message ?? "Nie udało się cofnąć podziału.",
          );
        },
      },
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="animate-fade-in flex flex-col items-center justify-center py-24">
        <Spinner size="lg" />
        <p className="mt-4 text-sm text-dim">
          Ładowanie podglądu rozliczenia...
        </p>
      </div>
    );
  }

  // Error state
  if (isError || !preview) {
    return (
      <div className="animate-fade-in space-y-4">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 text-sm text-sub transition-colors hover:text-main"
        >
          <ArrowLeft size={16} />
          Powrót do rozliczeń
        </button>
        <Alert variant="error" title="Błąd ładowania">
          {(error as Error)?.message ??
            "Nie udało się pobrać podglądu rozliczenia."}
        </Alert>
      </div>
    );
  }

  const overrideCount = Object.keys(overrides).length;

  return (
    <div className="animate-fade-in space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onClose}
            className="mt-1 rounded-lg p-1.5 text-sub transition-colors hover:bg-subtle hover:text-main"
            title="Powrót do rozliczeń"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-main">
              {preview.agentFullName}
            </h1>
            <p className="mt-0.5 text-sm capitalize text-dim">
              {formatPeriod(preview.year, preview.month)}
            </p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard
          label="Prowizja ogółem"
          value={formatMoney(preview.totalCommission)}
        />
        <SummaryCard
          label="Udział agenta"
          value={formatMoney(preview.agentShare)}
          sublabel={`${(preview.agentCommissionRate * 100).toFixed(0)}%`}
        />
        <SummaryCard
          label="Udział firmy"
          value={formatMoney(preview.companyShare)}
        />
        <SummaryCard label="Liczba polis" value={String(preview.policyCount)} />
      </div>

      {/* Override info */}
      {overrideCount > 0 && (
        <Alert variant="info" title="Zmiany przypisań">
          {overrideCount === 1
            ? "1 polisa zostanie przeniesiona do innego agenta."
            : `${overrideCount} polis zostanie przeniesionych do innego agenta.`}
        </Alert>
      )}

      {/* Policy table */}
      <Card padding="none">
        {preview.policies.length > 0 ? (
          <>
            {/* Collapse/Expand toggle */}
            <div className="flex items-center justify-end border-b border-edge-soft px-4 py-2">
              <button
                type="button"
                onClick={toggleAllGroups}
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-sub transition-colors hover:bg-subtle hover:text-main"
              >
                {allCollapsed ? (
                  <>
                    <ChevronsUpDown size={14} />
                    Rozwiń wszystkie
                  </>
                ) : (
                  <>
                    <ChevronsDownUp size={14} />
                    Zwiń wszystkie
                  </>
                )}
              </button>
            </div>

            <div className="overflow-auto max-h-[36rem]">
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-panel">
                  <tr className="border-b border-edge bg-subtle">
                    <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                      Nr polisy
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                      Klient
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                      Produkt
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                      Inkaso
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                      Prowizja
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                      Prowizja agenta
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                      Agent
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge-soft">
                  {groups.map((group) => (
                    <GroupSection
                      key={group.insurerName}
                      group={group}
                      isCollapsed={collapsedGroups.has(group.insurerName)}
                      onToggle={() => toggleGroup(group.insurerName)}
                      overrides={overrides}
                      onOverride={handleOverride}
                      onClearOverride={clearOverride}
                      onEditPolicy={(policy) => {
                        setEditingPolicy(policy);
                        setIsManualEntryOpen(true);
                      }}
                      onDeletePolicy={setDeletingPolicy}
                      onSplitPolicy={setSplittingPolicy}
                      onUnsplitPolicy={setUnsplittingPolicy}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-edge bg-subtle px-4 py-3">
              <span className="text-sm font-medium text-sub">
                Razem ({preview.policies.length}{" "}
                {preview.policies.length === 1 ? "polisa" : "polis"})
              </span>
              <span className="text-sm font-semibold text-main">
                {formatMoney(
                  preview.policies.reduce(
                    (sum, p) => sum + p.agentCommission,
                    0,
                  ),
                )}
              </span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-dim">
            <FileSpreadsheet size={32} className="mb-2" />
            <p className="text-sm">Brak polis w tym rozliczeniu</p>
          </div>
        )}
      </Card>

      {monthIncomplete && (
        <Alert variant="warning" title="Miesiąc nie jest kompletny">
          Zatwierdzanie jest zablokowane, dopóki nie sprawdzisz prowizji ze
          wszystkich firm. Brakuje:{" "}
          {missingInsurers.map((i) => i.insurerName).join(", ")}. Zaimportuj ich
          pliki albo w Import → Kontrola miesiąca potwierdź, że w tym miesiącu
          nie ma od nich prowizji.
        </Alert>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => {
            setEditingPolicy(null);
            setIsManualEntryOpen(true);
          }}
          leftIcon={<Plus size={16} />}
        >
          Dodaj wpis ręcznie
        </Button>
        <Button variant="outline" onClick={onClose}>
          Anuluj
        </Button>
        <Button
          variant="primary"
          onClick={handleApprove}
          isLoading={approve.isPending}
          disabled={approve.isPending || monthIncomplete}
          title={
            monthIncomplete
              ? "Najpierw sprawdź prowizje ze wszystkich firm"
              : undefined
          }
          leftIcon={<CheckCircle size={16} />}
          className="bg-green-600 hover:bg-green-700 focus-visible:ring-green-500"
        >
          Zatwierdź i wyślij e-mail
        </Button>
      </div>

      {/* Mutation error */}
      {approve.isError && (
        <Alert variant="error" title="Błąd zatwierdzania">
          {(approve.error as Error)?.message ??
            "Nie udało się zatwierdzić rozliczenia."}
        </Alert>
      )}

      {/* Manual entry modal */}
      <ManualEntryModal
        isOpen={isManualEntryOpen}
        onClose={() => {
          setIsManualEntryOpen(false);
          setEditingPolicy(null);
        }}
        onSubmit={(data) => {
          if (editingPolicy) {
            updateManualEntry.mutate(
              {
                settlementId: preview.settlementId,
                policyId: editingPolicy.policyId,
                data,
              },
              {
                onSuccess: () => {
                  setIsManualEntryOpen(false);
                  setEditingPolicy(null);
                  toast.success("Wpis zaktualizowany");
                },
                onError: (err) => {
                  toast.error(
                    (err as Error).message ??
                      "Nie udało się zaktualizować wpisu.",
                  );
                },
              },
            );
          } else {
            addManualEntry.mutate(
              { settlementId: preview.settlementId, data },
              {
                onSuccess: () => {
                  setIsManualEntryOpen(false);
                  toast.success("Wpis dodany");
                },
                onError: (err) => {
                  toast.error(
                    (err as Error).message ?? "Nie udało się dodać wpisu.",
                  );
                },
              },
            );
          }
        }}
        isLoading={addManualEntry.isPending || updateManualEntry.isPending}
        editingPolicy={editingPolicy}
      />

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deletingPolicy !== null}
        onClose={() => setDeletingPolicy(null)}
        title="Usuń wpis ręczny"
      >
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertTriangle
                size={20}
                className="text-red-600 dark:text-red-400"
              />
            </div>
            <div className="text-sm text-sub">
              <p>
                Czy na pewno chcesz usunąć wpis{" "}
                <strong className="text-main">
                  {deletingPolicy?.policyNumber}
                </strong>
                ?
              </p>
              <p className="mt-1 text-dim">
                Rozliczenie zostanie automatycznie przeliczone. Tej operacji nie
                można cofnąć.
              </p>
            </div>
          </div>
          {deleteManualEntry.isError && (
            <Alert variant="error" title="Błąd usuwania">
              {(deleteManualEntry.error as Error)?.message ??
                "Nie udało się usunąć wpisu."}
            </Alert>
          )}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeletingPolicy(null)}>
              Anuluj
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteManualEntry}
              isLoading={deleteManualEntry.isPending}
            >
              Usuń wpis
            </Button>
          </div>
        </div>
      </Modal>

      {/* Split commission modal */}
      <SplitCommissionModal
        isOpen={splittingPolicy !== null}
        onClose={() => {
          setSplittingPolicy(null);
          splitPolicy.reset();
        }}
        policy={splittingPolicy}
        contextAgentId={preview.agentId}
        contextAgentFullName={preview.agentFullName}
        onSubmit={(body) => {
          if (!splittingPolicy) return;
          splitPolicy.mutate(
            {
              settlementId: preview.settlementId,
              policyId: splittingPolicy.policyId,
              body,
            },
            {
              onSuccess: () => {
                toast.success(
                  "Podział zapisany — rozliczenia obu agentów przeliczone.",
                );
                setSplittingPolicy(null);
              },
              onError: (err) => {
                toast.error(
                  (err as Error).message ?? "Nie udało się zapisać podziału.",
                );
              },
            },
          );
        }}
        isLoading={splitPolicy.isPending}
        errorMessage={
          splitPolicy.isError
            ? ((splitPolicy.error as Error)?.message ?? null)
            : null
        }
      />

      {/* Unsplit confirmation modal */}
      <Modal
        isOpen={unsplittingPolicy !== null}
        onClose={() => setUnsplittingPolicy(null)}
        title="Cofnij podział prowizji"
      >
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle
                size={20}
                className="text-amber-600 dark:text-amber-400"
              />
            </div>
            <div className="text-sm text-sub">
              <p>
                Czy na pewno chcesz cofnąć podział prowizji dla polisy{" "}
                <strong className="text-main">
                  {unsplittingPolicy?.policyNumber}
                </strong>
                ?
              </p>
              <p className="mt-1 text-dim">
                Cała prowizja wróci do agenta prymarnego. Rozliczenia obu
                agentów zostaną przeliczone.
              </p>
            </div>
          </div>
          {unsplitPolicy.isError && (
            <Alert variant="error" title="Błąd">
              {(unsplitPolicy.error as Error)?.message ??
                "Nie udało się cofnąć podziału."}
            </Alert>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button
              variant="outline"
              onClick={() => setUnsplittingPolicy(null)}
              className="w-full sm:w-auto"
            >
              Anuluj
            </Button>
            <Button
              variant="primary"
              onClick={confirmUnsplit}
              isLoading={unsplitPolicy.isPending}
              className="w-full sm:w-auto"
            >
              Cofnij podział
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ========================================
// Summary Card (local to this view)
// ========================================

function SummaryCard({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: string;
  sublabel?: string;
}) {
  return (
    <div className="rounded-lg border border-edge-soft bg-panel px-4 py-3">
      <p className="text-xs text-dim">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <p className="text-lg font-semibold text-main">{value}</p>
        {sublabel && <span className="text-xs text-dim">{sublabel}</span>}
      </div>
    </div>
  );
}

// ========================================
// Group Section (local to this view)
// ========================================

function GroupSection({
  group,
  isCollapsed,
  onToggle,
  overrides,
  onOverride,
  onClearOverride,
  onEditPolicy,
  onDeletePolicy,
  onSplitPolicy,
  onUnsplitPolicy,
}: {
  group: InsurerGroup;
  isCollapsed: boolean;
  onToggle: () => void;
  overrides: Record<string, string>;
  onOverride: (policyId: string, newAgentId: string) => void;
  onClearOverride: (policyId: string) => void;
  onEditPolicy: (policy: ApprovalPolicyLine) => void;
  onDeletePolicy: (policy: ApprovalPolicyLine) => void;
  onSplitPolicy: (policy: ApprovalPolicyLine) => void;
  onUnsplitPolicy: (policy: ApprovalPolicyLine) => void;
}) {
  return (
    <>
      {/* Group header */}
      <tr
        className="cursor-pointer bg-slate-50 transition-colors hover:bg-slate-100 dark:bg-slate-800/50 dark:hover:bg-slate-800/80"
        onClick={onToggle}
      >
        <td colSpan={7} className="px-4 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isCollapsed ? (
                <ChevronRight size={16} className="text-dim" />
              ) : (
                <ChevronDown size={16} className="text-dim" />
              )}
              <span className="text-sm font-semibold text-main">
                {group.insurerName}
              </span>
              <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                {group.policies.length}{" "}
                {group.policies.length === 1 ? "polisa" : "polis"}
              </span>
            </div>
            <div className="flex items-center gap-6 text-xs tabular-nums text-sub">
              <span>
                Inkaso:{" "}
                <strong className="text-main">
                  {formatMoney(group.totalPremium)}
                </strong>
              </span>
              <span>
                Prowizja:{" "}
                <strong className="text-main">
                  {formatMoney(group.totalCommission)}
                </strong>
              </span>
              <span>
                Prowizja agenta:{" "}
                <strong className="text-main">
                  {formatMoney(group.totalAgentCommission)}
                </strong>
              </span>
            </div>
          </div>
        </td>
      </tr>

      {/* Policy rows */}
      {!isCollapsed &&
        group.policies.map((policy) => {
          const isOverridden = policy.policyId in overrides;
          return (
            <tr
              key={policy.policyId}
              className={clsx(
                "group transition-colors",
                isOverridden
                  ? "bg-amber-50 dark:bg-amber-950/20"
                  : "hover:bg-subtle",
              )}
            >
              <td className="px-4 py-3 text-sm font-medium text-main">
                <div className="flex flex-wrap items-center gap-2">
                  <span>{policy.policyNumber}</span>
                  {policy.isManualEntry && (
                    <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                      Ręczny
                    </span>
                  )}
                  {policy.split && (
                    <span
                      className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                      title={`Polisa współdzielona z ${policy.split.coAgentFullName}. Udział tego rozliczenia: ${Math.round(policy.split.myShare * 100)}%`}
                    >
                      <GitBranchPlus size={12} className="shrink-0" />
                      <span className="truncate">
                        Z {policy.split.coAgentFullName}
                      </span>
                      <span className="shrink-0">
                        · {Math.round(policy.split.myShare * 100)}%
                      </span>
                    </span>
                  )}
                  {/* Mobile: zawsze widoczne (touch — brak hover). Desktop: reveal-on-hover.
                      Większy tap target (p-1.5) na mobile dla ergonomii dotyku. */}
                  <span className="flex gap-0.5 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => onSplitPolicy(policy)}
                      className="rounded p-1.5 text-dim transition-colors hover:bg-blue-100 hover:text-blue-600 md:p-1 dark:hover:bg-blue-900/30"
                      title={
                        policy.split
                          ? "Edytuj podział prowizji"
                          : "Podziel prowizję z innym agentem"
                      }
                      aria-label={
                        policy.split
                          ? `Edytuj podział polisy ${policy.policyNumber}`
                          : `Podziel prowizję polisy ${policy.policyNumber}`
                      }
                    >
                      <GitBranchPlus size={16} className="md:size-3.5" />
                    </button>
                    {policy.split && (
                      <button
                        type="button"
                        onClick={() => onUnsplitPolicy(policy)}
                        className="rounded p-1.5 text-dim transition-colors hover:bg-amber-100 hover:text-amber-600 md:p-1 dark:hover:bg-amber-900/30"
                        title="Cofnij podział"
                        aria-label={`Cofnij podział polisy ${policy.policyNumber}`}
                      >
                        <Unlink size={16} className="md:size-3.5" />
                      </button>
                    )}
                    {policy.isManualEntry && (
                      <>
                        <button
                          type="button"
                          onClick={() => onEditPolicy(policy)}
                          className="rounded p-1.5 text-dim transition-colors hover:bg-blue-100 hover:text-blue-600 md:p-1 dark:hover:bg-blue-900/30"
                          title="Edytuj wpis"
                          aria-label={`Edytuj wpis ${policy.policyNumber}`}
                        >
                          <Pencil size={16} className="md:size-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeletePolicy(policy)}
                          className="rounded p-1.5 text-dim transition-colors hover:bg-red-100 hover:text-red-600 md:p-1 dark:hover:bg-red-900/30"
                          title="Usuń wpis"
                          aria-label={`Usuń wpis ${policy.policyNumber}`}
                        >
                          <Trash2 size={16} className="md:size-3.5" />
                        </button>
                      </>
                    )}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-main">
                {policy.clientName ?? "\u2014"}
              </td>
              <td className="px-4 py-3 text-sm text-main">
                {policy.productName ?? "\u2014"}
              </td>
              <td className="px-4 py-3 text-right text-sm tabular-nums text-main">
                {formatMoney(policy.premiumAmount)}
              </td>
              <td className="px-4 py-3 text-right text-sm tabular-nums text-main">
                {policy.split ? (
                  <span
                    title={`Pełna prowizja polisy: ${formatMoney(policy.commissionAmount)}`}
                  >
                    {formatMoney(policy.effectiveCommission)}
                  </span>
                ) : (
                  formatMoney(policy.effectiveCommission)
                )}
              </td>
              <td className="px-4 py-3 text-right text-sm tabular-nums text-main">
                {formatMoney(policy.agentCommission)}
              </td>
              <td className="px-4 py-3 text-sm">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1">
                    <AgentSelect
                      value={overrides[policy.policyId] ?? ""}
                      onChange={(agentId) =>
                        onOverride(policy.policyId, agentId)
                      }
                      placeholder="Bez zmian"
                      className="w-48"
                    />
                    {isOverridden && (
                      <button
                        type="button"
                        onClick={() => onClearOverride(policy.policyId)}
                        className="shrink-0 rounded p-1 text-amber-600 transition-colors hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/30"
                        title="Cofnij zmianę"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {policy.split && isOverridden && (
                    <p className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                      <span>
                        Zmiana agenta usunie podział z{" "}
                        {policy.split.coAgentFullName}.
                      </span>
                    </p>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
    </>
  );
}
